import orderByZ from 'utils/orderByZ'
import { getArtboardForPage } from 'EditorApp/selectors/artboards'

export function getSelectedPage(state) {
  const pageId = state.editor.selected.pageId
  const artboard = getArtboardForPage(state.markup.present, pageId)

  return artboard?.pages[pageId]
}

export function getSelectedElement(state) {
  const elementIds = state.editor.selected.elementIds
  if (elementIds.length !== 1) return null

  const page = getSelectedPage(state)
  if (!page) return null

  const element = page.elements[elementIds[0]]
  return element
}

export function getSelectedElementIds(state) {
  return state.editor.selected.elementIds
}

export function getSelectedElements(state) {
  const page = getSelectedPage(state)
  if (!page) return []

  return state.editor.selected.elementIds
    .map((elementId) => page.elements[elementId])
    .filter(Boolean)
}

export function elementIdsForGroup(page, elementId) {
  const element = page.elements[elementId]
  const groupId = element?.groupId

  if (!groupId) return [elementId]

  return Object.values(page.elements).reduce((ids, element) => {
    if (element.groupId === groupId) ids.push(element.id)

    return ids
  }, [])
}

export function elementsForGroup(page, groupId) {
  if (!groupId) return []

  return Object.values(page.elements).filter(
    (element) => element.groupId === groupId
  )
}

export function getGroupBounds(page, groupId) {
  return getSelectionBounds(elementsForGroup(page, groupId))
}

export function selectedElementsShareGroup(state) {
  const selectedElements = getSelectedElements(state)
  if (selectedElements.length <= 1) return false

  const [firstElement] = selectedElements
  if (!firstElement?.groupId) return false

  return selectedElements.every(
    (element) => element.groupId === firstElement.groupId
  )
}

export function selectedElementsMatchWholeGroup(state) {
  if (!selectedElementsShareGroup(state)) return false

  const page = getSelectedPage(state)
  const selectedElements = getSelectedElements(state)
  const groupId = selectedElements[0]?.groupId

  if (!page || !groupId) return false

  return selectedElements.length === elementsForGroup(page, groupId).length
}

export function selectedGroupId(state) {
  if (!selectedElementsMatchWholeGroup(state)) return null

  return getSelectedElements(state)[0]?.groupId || null
}

export function getSelectionBounds(elements) {
  if (!elements || elements.length === 0) return null

  const top = Math.min(...elements.map((element) => element.top))
  const left = Math.min(...elements.map((element) => element.left))
  const bottom = Math.max(
    ...elements.map((element) => element.top + element.height)
  )
  const right = Math.max(
    ...elements.map((element) => element.left + element.width)
  )

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  }
}

export function getTheme(state, newTheme = null) {
  const houseStyle = state.config.houseStyle
  const themes = houseStyle.themes

  const selectedTheme = state.markup.present.theme

  return themes[newTheme || selectedTheme || houseStyle.defaultTheme]
}

export function getElementTheme(state, elementType) {
  return getTheme(state).elements[elementType]
}

export function getThemesForElementType(state, type) {
  const houseStyle = state.config.houseStyle
  const themes = houseStyle.themes

  const typeThemes = {}

  for (const [name, theme] of Object.entries(themes)) {
    const elementTheme = theme.elements[type]

    if (elementTheme) {
      typeThemes[name] = elementTheme
    }
  }

  return typeThemes
}

// text chunk helpers
export const stateToEditorFormat = (chunks) => {
  return chunks.map(({ text }) => {
    const content = text || '(missing)'

    return {
      type: 'paragraph',
      children: [{ text: content }],
    }
  })
}

export const editorFormatToState = (editorValue) => {
  return editorValue.map(({ children: [chunk] }) => chunk)
}

export const pageElements = (page) => {
  return orderByZ(page.elements).filter((e) => !e.disabled)
}
